import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';

const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;

export default function BankReconciliation() {
  const { token } = useAuth();
  const [connector, setConnector] = useState('plaid');
  const [institutionId, setInstitutionId] = useState('demo-bank');
  const [publicToken, setPublicToken] = useState('sandbox-public');
  const [accountId, setAccountId] = useState('primary');
  const [transactions, setTransactions] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const [txs, suggestionsResponse] = await Promise.all([
        api.get('/banking/transactions', token),
        api.get('/banking/suggestions', token)
      ]);
      setTransactions(txs || []);
      setSuggestions(suggestionsResponse || []);
    } catch (error) {
      toast.error('Unable to load banking data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTransactions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    try {
      await api.post(
        '/banking/connect',
        { connector, institution_id: institutionId, public_token: publicToken, account_id: accountId },
        token
      );
      toast.success('Connector linked');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to link');
    }
  };

  const sync = async () => {
    setLoading(true);
    try {
      await api.post('/banking/sync', { connector, institution_id: institutionId }, token);
      toast.success('Sync complete');
      await loadTransactions();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Sync failed');
    } finally {
      setLoading(false);
    }
  };

  const match = async (transactionId, invoiceId, paymentId) => {
    try {
      await api.post(`/banking/transactions/${transactionId}/match`, { invoice_id: invoiceId, payment_id: paymentId }, token);
      toast.success('Matched');
      await loadTransactions();
    } catch (error) {
      toast.error('Match failed');
    }
  };

  const ignore = async (transactionId) => {
    await api.post(`/banking/transactions/${transactionId}/ignore`, {}, token);
    toast.success('Ignored');
    await loadTransactions();
  };

  const renderSuggestion = (transactionId) => {
    const suggestion = suggestions.find((s) => s.transaction_id === transactionId);
    if (!suggestion) return null;
    const label = suggestion.invoice_id ? `Invoice ${suggestion.invoice_id}` : `Payment ${suggestion.payment_id}`;
    return (
      <div className="space-x-2">
        <Badge variant="secondary">Suggested: {label}</Badge>
        <Button size="sm" variant="outline" onClick={() => match(transactionId, suggestion.invoice_id, suggestion.payment_id)}>
          Match
        </Button>
      </div>
    );
  };

  const reconciliationStats = useMemo(() => {
    const total = transactions.length;
    const matched = transactions.filter((t) => t.match_state === 'matched').length;
    const ignored = transactions.filter((t) => t.match_state === 'ignored').length;
    return { total, matched, ignored };
  }, [transactions]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Bank Reconciliation</h1>
          <p className="text-muted-foreground">Sync feeds, classify transactions, and match to invoices or payments.</p>
        </div>
        <div className="space-x-2">
          <Button onClick={sync} disabled={loading}>Sync feed</Button>
          <Button variant="outline" onClick={loadTransactions} disabled={loading}>Refresh</Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Connect feed</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-4 gap-4">
          <div>
            <label className="text-sm font-medium">Connector</label>
            <select className="w-full border rounded-md h-10 px-3" value={connector} onChange={(e) => setConnector(e.target.value)}>
              <option value="plaid">Plaid</option>
              <option value="yodlee">Yodlee</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Institution ID</label>
            <Input value={institutionId} onChange={(e) => setInstitutionId(e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-medium">Account ID</label>
            <Input value={accountId} onChange={(e) => setAccountId(e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-medium">Public token</label>
            <div className="flex gap-2">
              <Input value={publicToken} onChange={(e) => setPublicToken(e.target.value)} />
              <Button onClick={connect}>Link</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Reconciliation status</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center gap-8">
          <div>
            <div className="text-3xl font-semibold">{reconciliationStats.total}</div>
            <p className="text-sm text-muted-foreground">Total transactions</p>
          </div>
          <Separator orientation="vertical" className="h-12" />
          <div>
            <div className="text-3xl font-semibold">{reconciliationStats.matched}</div>
            <p className="text-sm text-muted-foreground">Matched</p>
          </div>
          <Separator orientation="vertical" className="h-12" />
          <div>
            <div className="text-3xl font-semibold">{reconciliationStats.ignored}</div>
            <p className="text-sm text-muted-foreground">Ignored</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((tx) => (
                <TableRow key={tx.transaction_id}>
                  <TableCell>{new Date(tx.occurred_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <div className="font-medium">{tx.description}</div>
                    <div className="text-xs text-muted-foreground">{tx.transaction_id}</div>
                  </TableCell>
                  <TableCell>{formatCurrency(tx.amount)}</TableCell>
                  <TableCell>
                    <Badge variant={tx.match_state === 'matched' ? 'default' : tx.match_state === 'ignored' ? 'secondary' : 'outline'}>
                      {tx.match_state}
                    </Badge>
                  </TableCell>
                  <TableCell className="space-x-2">
                    {renderSuggestion(tx.transaction_id)}
                    {tx.match_state !== 'ignored' && (
                      <Button size="sm" variant="ghost" onClick={() => ignore(tx.transaction_id)}>
                        Ignore
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
